import {Component, OnInit, ViewChild} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {LayoutFieldExt} from '@docma-mm-core/layout/layout-field';
import {LayoutFilter} from '@docma-mm-core/layout/layout-filter';
import {Layout} from '@docma-mm-core/layout/layout';
import {GetRowIdParams, GridApi, IRowDragItem} from 'ag-grid-community';
import {SearchOptions} from '@mydocma/component-library/src/lib/search/options/search-options';
import {AgGridAngular} from 'ag-grid-angular';
import {SearchFilterService} from '@mydocma/component-library/src/lib/search';
import {FilterableDataSource} from '@mydocma/component-library/src/lib/search/filterable-data-source';
import {CellValueChangedEvent} from 'ag-grid-community/dist/lib/main';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {LayoutEditFiltersService} from '../services/layout-edit-filters.service';
import {LayoutFilterCellRendererComponent} from './layout-filter-cell-renderer/layout-filter-cell-renderer.component';
import {BehaviorSubject, Subject} from 'rxjs';
import {LayoutFilterScopeCellRendererComponent} from './layout-filter-scope-cell-renderer/layout-filter-scope-cell-renderer.component';
import {Project} from '@docma-mm-core/project/project';
import {User} from '@docma-mm-core/user/user';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {TranslateService} from '@ngx-translate/core';
import {MydocmaGridOptions} from '@mydocma/component-library/src/lib/grid';

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-layout-edit-filters',
  templateUrl: './layout-edit-filters.component.html',
  styleUrls: ['./layout-edit-filters.component.scss'],
  providers: [LayoutEditFiltersService]
})
export class LayoutEditFiltersComponent implements OnInit {

  @ViewChild('grid') grid: AgGridAngular;

  editArgs?: {Layout: Layout, Fields: LayoutFieldExt[], Filters: LayoutFilter[], IsNew: boolean, project: Project, user: User};
  filters: LayoutFilter[];

  currentFiltersSubject = new BehaviorSubject<LayoutFilter[]>([]);
  isAssignedAllSubject = new BehaviorSubject<boolean>(false);
  isAssignedAll$ = this.isAssignedAllSubject.asObservable();

  valueChanged = new Subject();

  private gridApi: GridApi;

  gridOptions: MydocmaGridOptions;
  searchOptions: SearchOptions;

  searchFilters: string[] = [];

  constructor(public readonly filtersService: LayoutEditFiltersService,
              private readonly dialogService: PromptDialogService,
              private readonly translateService: TranslateService) {

    this.searchOptions = {
      allowMultipleFilter: true,
      onReady: (searchFilterService: SearchFilterService) => {
        searchFilterService.dataSource = { data: [], doesItemPassFilter:  (item: any, filters: string[]) => true } as FilterableDataSource;
        searchFilterService.filters$
          .subscribe((filters: string[]) => {
            this.searchFilters = filters.filter(x => x != null && x != "").map(x => x.toLowerCase());
            if (this.gridApi) {
              this.setDataSource();
            }
          });

      }
    };

    this.gridOptions = {
      rowSelection: 'single',
      suppressRowClickSelection: true,
      context: { componentParent: this },
      rowDragManaged: true,
      suppressRowDrag: true,
      animateRows: true,
      enableFillHandle: true,
      rowDragMultiRow: false,
      rowDragEntireRow: false,
      headerHeight: 0,
      suppressLoadingOverlay: true,
      columnDefs: [
        {
          colId: 'IsAssigned',
          resizable: false,
          pinned: false,
          suppressAutoSize: false,
          suppressSizeToFit: true,
          suppressMovable: true,
          field: 'IsAssigned',
          headerName: '',
          cellRenderer: LayoutFilterCellRendererComponent,
          rowDrag: (params) => {
            const lf: LayoutFilter = params.node.data;
            return (lf.IsAssigned);
          },
          rowDragText: (params: IRowDragItem, dragItemCount: number) => {
            const lf: LayoutFilter = params.rowNode.data;
            return lf.Filter.Name;
          },
          width: 72
        },
        {
          colId: 'Scope',
          resizable: false,
          pinned: false,
          suppressAutoSize: false,
          suppressSizeToFit: true,
          suppressMovable: true,
          field: 'Filter.Scope',
          headerName: '',
          cellRenderer: LayoutFilterScopeCellRendererComponent,
          width: 50
        },
        {
          colId: 'Name',
          resizable: true,
          pinned: false,
          suppressAutoSize: false,
          suppressSizeToFit: false,
          suppressMovable: true,
          field: 'Filter.Name',
          headerName: 'Name',
        }],
      rowClassRules: {
        'filter-row': params => { return true; },
      },
      getRowId: (params: GetRowIdParams) => {
        return params.data.Filter.Id;
      },
      onGridReady: (params) => {
        this.gridApi = params.api;

        this.setDataSource();
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        const lf: LayoutFilter = event.node.data;

        // IsAssigned toggled
        if (event.column.getColId() === 'IsAssigned') {
          this.setDataSource();
          this.commit();
        }
      }
    } as MydocmaGridOptions;
  }

  initialize(editArgs?: {Layout: Layout, Fields: LayoutFieldExt[], Filters: LayoutFilter[], IsNew: boolean, project: Project, user: User}) {
    this.editArgs = editArgs;
    this.filters = JSON.parse(JSON.stringify(this.editArgs.Filters));
    this.setDataSource();
  }

  onShow() {
    this.gridOptions.api.sizeColumnsToFit();
  }

  onScopeChanged(scope: EntityScopeEnum) {
    let hasInvalidFilters: boolean = false;

    for (const f of this.filters) {
      if ((scope === EntityScopeEnum.Global && (f.Filter.Project != null || f.Filter.User != null))
        || (scope === EntityScopeEnum.Project && f.Filter.User != null)
        || (scope === EntityScopeEnum.User && f.Filter.Project != null)) {
        if (f.IsAssigned) {
          f.IsAssigned = false;
          hasInvalidFilters = true;
        }
      }
    }

    if (hasInvalidFilters) {
      this.commit();
      this.dialogService.showNotificationDialog({ title: this.translateService.instant('MM.layout.messageChangeLayoutTypeTitle'), content: this.translateService.instant('MM.layout.messageChangeLayoutType')})
    }

    this.setDataSource();
    this.gridApi.redrawRows();
  }

  setDataSource() {
    if (!this.filters)
      return;

    const sortedFilers = [...this.filters].filter(x =>
      ((this.editArgs.Layout.Scope === EntityScopeEnum.Global && x.Filter.User == null && x.Filter.Project == null)
      || (this.editArgs.Layout.Scope === EntityScopeEnum.Project && ((x.Filter.User == null && x.Filter.Project == null) || x.Filter.Project?.Id === this.editArgs.project.Id))
        || (this.editArgs.Layout.Scope === EntityScopeEnum.User && ((x.Filter.User == null && x.Filter.Project == null) || x.Filter.User?.Id === this.editArgs.user.Id)))

      && (this.searchFilters.length === 0
      || (this.searchFilters.some(fil => x.Filter.Name.toLowerCase().includes(fil))))
    ).sort((x, y) =>
      (y.IsAssigned ? 1 : 0) - (x.IsAssigned ? 1 : 0)
      || (y.Filter.Scope === EntityScopeEnum.User ? 1 : 0) - (x.Filter.Scope === EntityScopeEnum.User ? 1 : 0)
      || (y.Filter.Scope === EntityScopeEnum.Project ? 1 : 0) - (x.Filter.Scope === EntityScopeEnum.Project ? 1 : 0)
      || (y.Filter.Scope === EntityScopeEnum.Global ? 1 : 0) - (x.Filter.Scope === EntityScopeEnum.Global ? 1 : 0)
      || x.Filter.Name.localeCompare(y.Filter.Name)
    );

    this.gridOptions.api.setRowData(sortedFilers);
    this.gridOptions.api.clearFocusedCell();
    this.gridOptions.api.sizeColumnsToFit();

    this.currentFiltersSubject.next(sortedFilers);
      
    this.filtersService.updateSelectedFiltersCount(sortedFilers.filter(f => f.IsAssigned).length);
  }

  private commit() {
    let hasChanges = false;

    for (const f of this.editArgs.Filters) {
      const _f = this.filters.find(x => x.Filter.Id === f.Filter.Id);
      if (f.IsAssigned !== _f.IsAssigned) {
        const lfIdx = this.editArgs.Layout.LayoutFilters.findIndex(lf => lf.Filter.Id === f.Filter.Id);
        if (lfIdx === -1 && _f.IsAssigned) {
          this.editArgs.Layout.LayoutFilters.push(f);
        }
        else if (lfIdx !== -1 && !_f.IsAssigned) {
          this.editArgs.Layout.LayoutFilters.splice(lfIdx, 1);
        }

        f.IsAssigned = _f.IsAssigned;
        hasChanges = true;
      }
    }

    if (hasChanges) {
      this.valueChanged.next(null)
    }
  }

  ngOnInit(): void {
    this.currentFiltersSubject
      .pipe(untilDestroyed(this))
      .subscribe(currentFilters =>  {
        if (currentFilters.length > 0 && currentFilters.every(x => x.IsAssigned)) {
          this.isAssignedAllSubject.next(true);
        }
        else {
          this.isAssignedAllSubject.next(false);
        }
      });
  }

  onActiveAllToggled() {
    this.isAssignedAllSubject.next(!this.isAssignedAllSubject.value);
    let newSortId: number;
    let hasChanges: boolean = false;

    this.currentFiltersSubject.value.forEach(f => {
      var _f = this.filters.find(x => x.Filter.Id === f.Filter.Id);
      if (_f) {
        _f.IsAssigned = this.isAssignedAllSubject.value;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.gridApi.redrawRows();
      this.commit();
    }
  }
}
